/**
 * "지금 화면에 핀이 하나도 없는가" 판정 — 순수 함수.
 *
 * 왜 필요한가: 지도 "현 위치" 버튼은 사용자를 **자기 위치**로 데려간다. 한국에서
 * 일본 지점 허브를 보던 사람이 그 버튼을 누르면 화면에 핀이 0개가 되고, 지도를
 * 손으로 1,000km 끌어 되돌아오는 것 말고는 길이 없다. 그 상태를 감지해 "지점이
 * 있는 곳으로" 버튼을 띄우기 위한 판정이다.
 *
 * 지도 API 를 import 하지 않는다 — bounds 를 평범한 숫자 4개로 받아 테스트가
 * google.maps 없이 규칙만 검증할 수 있게 한다.
 */

/** `google.maps.LatLngBounds.toJSON()` 과 같은 모양. */
export interface ViewportBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface MarkerPoint {
  lat: number;
  lng: number;
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/** 한 좌표가 viewport 안에 있는가. 날짜변경선을 넘는 bounds(east < west)도 처리. */
export function isPointInBounds(
  point: MarkerPoint,
  bounds: ViewportBounds,
): boolean {
  if (!isFiniteNumber(point?.lat) || !isFiniteNumber(point?.lng)) return false;
  if (point.lat < bounds.south || point.lat > bounds.north) return false;

  // 날짜변경선을 걸친 viewport 는 east < west 로 온다(예: west 170, east -170).
  // 그때는 "둘 중 하나라도 만족"이 안쪽이다.
  return bounds.west <= bounds.east
    ? point.lng >= bounds.west && point.lng <= bounds.east
    : point.lng >= bounds.west || point.lng <= bounds.east;
}

/**
 * **핀은 있는데 화면 안에는 하나도 없는가.**
 *
 * - 핀이 아예 없으면 false — 보여 줄 곳이 없으니 버튼도 뜨면 안 된다.
 * - bounds 를 아직 모르면(지도 준비 전) false — 판정 근거가 없을 때 버튼을
 *   띄우면 지도가 뜨는 순간 잠깐 깜빡인다.
 * - 좌표가 깨진 핀은 "없는 것"으로 본다(그 핀은 어차피 그려지지 않는다).
 */
export function hasNoMarkerInBounds(
  markers: MarkerPoint[] | null | undefined,
  bounds: ViewportBounds | null | undefined,
): boolean {
  if (!bounds) return false;
  const usable = (markers ?? []).filter(
    (m) => isFiniteNumber(m?.lat) && isFiniteNumber(m?.lng),
  );
  if (usable.length === 0) return false;
  return !usable.some((m) => isPointInBounds(m, bounds));
}
